import { computed, ComputedRef } from 'vue'
import type { MarketplaceIntegration } from '@/types/marketplace'
import { getUxPageSettings } from './uxSettings'
import type {
    UxPageAboutCard,
    UxPageConnectCard,
    UxPageHeaderBadge,
    UxPageHeaderCard,
    UxPageOtherIntegrationRef,
    UxPageSettings,
    UxPageSkillCard,
} from './uxSettings'

export type IntegrationPageBadgeView = {
    text: string
    className?: string
    style?: Record<string, string>
}

function resolveUxValue<T>(uxValue: T | null | undefined, fallback: T): T {
    if (uxValue === null || uxValue === undefined) {
        return fallback
    }
    return uxValue
}

function resolveOptionalUxValue<T>(uxValue: T | null | undefined): T | undefined {
    if (uxValue === null || uxValue === undefined) {
        return undefined
    }
    return uxValue
}

function guessIconByName(name: string): string {
    const normalized = name.toLowerCase()
    if (normalized.includes('обуч') || normalized.includes('интенсив')) return '🎓'
    if (normalized.includes('kpi') || normalized.includes('показател')) return '📊'
    if (normalized.includes('то crm')) return '🔧'
    if (normalized.includes('crm') || normalized.includes('срм')) return '👑'
    if (normalized.includes('автопилот')) return '💄'
    if (normalized.includes('аудиорассылки') || normalized.includes('звонобот')) return '📞'
    if (normalized.includes('мультичат')) return '💬'
    if (normalized.includes('горячие окна')) return '🔥'
    if (normalized.includes('диагностика')) return '🔍'
    if (normalized.includes('каскад') || normalized.includes('отчёт 2.0')) return '📈'
    if (normalized.includes('опз 2.0')) return '🔁'
    if (normalized.includes('вебинары')) return '🎓'
    return '📦'
}

function buildApiBadgeView(integration: MarketplaceIntegration): IntegrationPageBadgeView | null {
    const text = integration.badge?.text
    if (!text) return null

    const normalized = text.toLowerCase()
    const withStar = text.trim().startsWith('★') ? text : `★ ${text}`

    if (normalized.includes('хит')) return { className: 'bd-hit', text: withStar }
    if (normalized.includes('новин') || normalized.includes('под ключ')) return { className: 'bd-new', text: withStar }
    if (normalized.includes('рекоменду')) return { className: 'bd-rec', text }
    if (normalized.includes('тренд')) return { className: 'bd-trend', text }
    return { className: 'bd-default', text }
}

function buildUxBadgeView(badge: UxPageHeaderBadge): IntegrationPageBadgeView | null {
    if (badge.text === null || badge.text === undefined || badge.text === '') {
        return null
    }

    return {
        text: badge.text,
        style: {
            ...(badge.bcg ? { background: badge.bcg } : {}),
            ...(badge.color ? { color: badge.color } : {}),
        },
    }
}

function resolveHeaderBadge(
    integration: MarketplaceIntegration,
    headerCard?: UxPageHeaderCard,
): IntegrationPageBadgeView | null {
    const uxBadge = headerCard?.badge
    if (uxBadge && uxBadge.text !== null && uxBadge.text !== undefined) {
        return buildUxBadgeView(uxBadge)
    }
    return buildApiBadgeView(integration)
}

export function useIntegrationPageUx(
    integration: ComputedRef<MarketplaceIntegration | undefined>,
) {
    const pageUx = computed((): UxPageSettings | null => {
        const id = integration.value?.id
        if (!id) return null
        return getUxPageSettings(id)
    })

    const headerIcon = computed(() => {
        const integrationValue = integration.value
        if (!integrationValue) return '📦'
        return resolveUxValue(pageUx.value?.headerCard?.icon, guessIconByName(integrationValue.name))
    })

    const headerBadge = computed(() => {
        const integrationValue = integration.value
        if (!integrationValue) return null
        return resolveHeaderBadge(integrationValue, pageUx.value?.headerCard)
    })

    const headerCategory = computed(() => {
        const integrationValue = integration.value
        if (!integrationValue) return 'Маркетплейс'
        return resolveUxValue(pageUx.value?.headerCard?.tags, integrationValue.tags?.[0] || 'Маркетплейс')
    })

    const headerTitle = computed(() => {
        const integrationValue = integration.value
        if (!integrationValue) return ''
        return resolveUxValue(pageUx.value?.headerCard?.title, integrationValue.name)
    })

    const headerDescription = computed(() => {
        const integrationValue = integration.value
        if (!integrationValue) return ''
        return resolveUxValue(
            pageUx.value?.headerCard?.shortDescription,
            integrationValue.descriptionShort,
        )
    })

    const headerExtraInfoHtml = computed(() =>
        resolveOptionalUxValue(pageUx.value?.headerCard?.extraInfoHtml),
    )

    const aboutCard = computed((): UxPageAboutCard | null => pageUx.value?.aboutCard ?? null)

    const aboutDescriptions = computed(() => {
        const integrationValue = integration.value
        const about = pageUx.value?.aboutCard
        if (about) {
            const paragraphs = [about.description, about.secondDescription].filter(Boolean)
            if (paragraphs.length) return paragraphs
        }
        if (!integrationValue) return []
        const fromApi = [integrationValue.descriptionFull, integrationValue.descriptionShort].filter(
            (value, index, list) => Boolean(value) && list.indexOf(value) === index,
        )
        return fromApi
    })

    const aboutFacts = computed(() => pageUx.value?.aboutCard?.facts ?? [])

    const useUxAboutFacts = computed(() => Boolean(pageUx.value?.aboutCard?.facts?.length))

    const skillsCards = computed((): UxPageSkillCard[] => pageUx.value?.skillsCards ?? [])

    const skillsSectionSubtitle = computed(() => {
        const integrationValue = integration.value
        if (!integrationValue || !skillsCards.value.length) return ''
        return `Что вы получаете после подключения «${integrationValue.name}».`
    })

    const connectCard = computed((): UxPageConnectCard | null => pageUx.value?.connectCard ?? null)

    const connectFacts = computed(() => connectCard.value?.facts ?? [])

    const connectPartner = computed(() => connectCard.value?.partner ?? null)

    const showDhOfficial = computed(
        () => Boolean(connectCard.value?.dhOfficial) && !connectCard.value?.partner,
    )

    const pricingTiers = computed(() => connectCard.value?.pricing ?? null)

    const otherIntegrationRefs = computed((): UxPageOtherIntegrationRef[] =>
        pageUx.value?.otherIntegrations ?? [],
    )

    const otherIntegrationsTitle = computed(
        () => pageUx.value?.otherIntegrationsTitle ?? 'Другие решения маркетплейса',
    )

    const hasUxPage = computed(() => Boolean(pageUx.value && Object.keys(pageUx.value).length))

    return {
        pageUx,
        hasUxPage,
        headerIcon,
        headerBadge,
        headerCategory,
        headerTitle,
        headerDescription,
        headerExtraInfoHtml,
        aboutCard,
        aboutDescriptions,
        aboutFacts,
        useUxAboutFacts,
        skillsCards,
        skillsSectionSubtitle,
        connectCard,
        connectFacts,
        connectPartner,
        showDhOfficial,
        pricingTiers,
        otherIntegrationRefs,
        otherIntegrationsTitle,
    }
}
